import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import Seo from "../components/Seo";
import { ArrowRight, Calendar } from "lucide-react";

// Each post maps to an existing SeoPage slug so the article actually opens.
// Featured = the 3 freshest, shown in the hero strip.
const POSTS = [
  {
    slug: "ai-domain-names-for-sale",
    title: "AI domain names for sale — brandable .ai inventory in 2026",
    excerpt: "Every brandable one-word .ai is gone. Here's where the 2-syllable .ai gems still live, plus how aftermarket pricing actually works.",
    date: "Jun 14, 2026",
    tag: "Premium picks",
    read: "6 min",
    featured: true,
  },
  {
    slug: "expiring-domain-names",
    title: "Expiring domain names — how to grab them before they re-list",
    excerpt: "100,000 domains expire daily. Most are junk; a small slice are brandable gems. Here's the 4-stage expiry pipeline and where to bid.",
    date: "Jun 10, 2026",
    tag: "Buying guide",
    read: "6 min",
    featured: true,
  },
  {
    slug: "saas-domains-for-sale",
    title: "SaaS domains for sale — brandable .com & .io picks",
    excerpt: "Stop wasting 3 weeks brainstorming. Here's the 2026 catalogue of B2B-ready domain names sorted by category and price.",
    date: "Jun 4, 2026",
    tag: "Premium picks",
    read: "5 min",
    featured: true,
  },
  {
    slug: "startup-domains-for-sale",
    title: "Startup domains for sale — brandable names under $30",
    excerpt: "You don't need a $50k .com to launch. Hundreds of brandable startup domains list under $30 on the aftermarket every single day.",
    date: "May 28, 2026",
    tag: "Buying guide",
    read: "5 min",
  },
  {
    slug: "cheap-domain-names",
    title: "Cheap domain names that still sound great in 2026",
    excerpt: "Cheap doesn't have to mean ugly. The smartest cheap path isn't a $1 .xyz — it's a brandable .com from the expired closeout pool.",
    date: "May 20, 2026",
    tag: "Pricing analysis",
    read: "4 min",
  },
  {
    slug: "how-to-transfer-domain",
    title: "How to transfer a domain (and save 40% on renewals)",
    excerpt: "GoDaddy renews .com at $21.99. Cloudflare renews the same domain at $9.59 forever. Here's the exact step-by-step.",
    date: "May 12, 2026",
    tag: "Guides",
    read: "6 min",
  },
  {
    slug: "namecheap-vs-godaddy-vs-cloudflare",
    title: "Namecheap vs GoDaddy vs Cloudflare vs Porkbun — 2026 comparison",
    excerpt: "Head-to-head on price, renewal cost, WHOIS privacy, support speed and trust score. With recommendations by use case.",
    date: "May 6, 2026",
    tag: "Comparison",
    read: "8 min",
  },
  {
    slug: "io-vs-com-vs-ai",
    title: ".io vs .com vs .ai — which TLD should your startup pick?",
    excerpt: "Trust signal, price, investor perception, SEO impact — laid out side-by-side with a stage-by-stage recommendation.",
    date: "Apr 28, 2026",
    tag: "Naming",
    read: "5 min",
  },
  {
    slug: "renewal-traps-to-avoid",
    title: "5 domain renewal traps that cost founders hundreds",
    excerpt: "The $0.99 teaser, auto-renew at retail, registry-premium markups — five traps every founder hits, and how to dodge all of them.",
    date: "Apr 20, 2026",
    tag: "Pricing analysis",
    read: "4 min",
  },
  {
    slug: "whois-privacy-explained",
    title: "What is WHOIS privacy — and do you actually need it?",
    excerpt: "Why your inbox floods with spam the day you register a new domain — and the one click that fixes it.",
    date: "Apr 14, 2026",
    tag: "Security",
    read: "4 min",
  },
  {
    slug: "cheapest-com-domain",
    title: "The cheapest .com domain you can buy right now",
    excerpt: "Spaceship at $7.49 first-year, Cloudflare at $9.59 forever — and the 3-year totals across every major host.",
    date: "Apr 5, 2026",
    tag: "Pricing analysis",
    read: "3 min",
  },
  {
    slug: "buy-domain-cheap",
    title: "Buy a domain cheap — and keep it cheap (2026 buyer's guide)",
    excerpt: "Most cheap-domain offers are bait. The $1 first-year renews at $25. Here's how to actually save over the lifetime of a domain.",
    date: "Mar 28, 2026",
    tag: "Buying guide",
    read: "6 min",
  },
  {
    slug: "cheap-domain-and-hosting",
    title: "Cheap domain and hosting — which bundles actually save money",
    excerpt: "Hostinger and Namecheap throw in free domains with hosting. We did the math on which bundle is the best deal over 3 years.",
    date: "Mar 22, 2026",
    tag: "Pricing analysis",
    read: "5 min",
  },
  {
    slug: "cheap-domain-and-email",
    title: "Cheap domain + business email under $20/year",
    excerpt: "you@yourbrand.com is the cheapest credibility upgrade for a new business. Here's how the major hosts bundle email with domains.",
    date: "Mar 15, 2026",
    tag: "Email",
    read: "4 min",
  },
  {
    slug: "cheap-domain-provider",
    title: "The best cheap domain providers in 2026 — ranked",
    excerpt: "Not all cheap registrars are equal. We benchmarked the major providers on price, renewal honesty, support and WHOIS privacy.",
    date: "Mar 8, 2026",
    tag: "Pricing analysis",
    read: "7 min",
  },
  {
    slug: "domain-price-comparison",
    title: "Domain price comparison — every host, one view",
    excerpt: "How our live engine works: TLDSpy + direct APIs, RDAP verification, and registry-premium detection — refreshed daily.",
    date: "Feb 27, 2026",
    tag: "Inside Dompryce",
    read: "3 min",
  },
  {
    slug: "cheapest-domain-registration",
    title: "Cheapest domain registration in 2026 — full ranking",
    excerpt: "Sorted by first-year price, lowest 3-year total, and lowest renewal — pick the column that fits how long you'll hold the domain.",
    date: "Feb 20, 2026",
    tag: "Pricing analysis",
    read: "5 min",
  },
  {
    slug: "best-ai-domains",
    title: "The best .ai domains you can still buy in 2026",
    excerpt: "We crawled the major aftermarkets. Hand-picked brandable .ai names worth grabbing this quarter, sorted by price tier and category.",
    date: "Feb 14, 2026",
    tag: "Premium picks",
    read: "5 min",
  },
  {
    slug: "cheap-domain-registrars",
    title: "The cheapest registrars in 2026 (without the renewal trap)",
    excerpt: "Spaceship's promo $7.49 .com is a steal — but only if you renew with Cloudflare. Full breakdown inside.",
    date: "Feb 8, 2026",
    tag: "Pricing analysis",
    read: "4 min",
  },
  {
    slug: "startup-domain-names",
    title: "Startup naming playbook: how to pick a name you won't regret",
    excerpt: "A ten-minute framework used by repeat founders to land memorable names that pass the bar test.",
    date: "Jan 31, 2026",
    tag: "Naming",
    read: "6 min",
  },
  {
    slug: "best-dot-ai-domains",
    title: "Why .ai is the new .com for AI-first startups",
    excerpt: "Adoption is up 38% YoY. Here's what's driving the surge and how to price your transfer.",
    date: "Jan 22, 2026",
    tag: "Trends",
    read: "4 min",
  },
  {
    slug: "hotel-tech-domains",
    title: "Hotel tech naming: .hotel, .stay, or just stick with .com?",
    excerpt: "We tested 30 hotel tech domains for click-through and trust — the answer might surprise you.",
    date: "Jan 12, 2026",
    tag: "Hospitality",
    read: "4 min",
  },
];

const TAGS = ["All", ...Array.from(new Set(POSTS.map((p) => p.tag)))];

export default function Blog() {
  const [active, setActive] = useState("All");
  const filtered = useMemo(
    () => (active === "All" ? POSTS : POSTS.filter((p) => p.tag === active)),
    [active]
  );
  const featured = POSTS.filter((p) => p.featured);
  const rest = filtered.filter((p) => !featured.includes(p) || active !== "All");

  return (
    <Layout>
      <Seo
        title="Domain blog — guides, naming tips & registrar reviews"
        description="Practical, founder-focused guides on cheap domain registration, AI naming, TLD trends, WHOIS privacy and registrar comparisons."
        keywords="domain blog, registrar reviews, domain naming guide, .ai domain trends, domain transfer guide, whois privacy"
        path="/blog"
      />
      <section data-testid="blog-page">
        <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-400">Insights</div>
        <h1 className="mt-2 font-display text-4xl font-bold tracking-tight text-zinc-900 md:text-5xl">
          The Dompryce journal.
        </h1>
        <p className="mt-3 max-w-2xl text-zinc-600">
          Pricing analysis, naming playbooks, transfer guides, and curated drops — written for builders.
        </p>

        {/* Featured strip — only on All view */}
        {active === "All" && (
          <div className="mt-10 grid grid-cols-1 gap-4 lg:grid-cols-3" data-testid="blog-featured">
            {featured.map((p, idx) => (
              <Link
                key={p.slug}
                to={`/${p.slug}`}
                data-testid={`blog-${p.slug}`}
                className={`group flex flex-col rounded-2xl border p-7 transition-all hover:-translate-y-0.5 hover:shadow-md ${
                  idx === 0
                    ? "bg-zinc-900 text-white border-zinc-900 lg:row-span-2 lg:col-span-1"
                    : "border-zinc-100 bg-white hover:border-indigo-200 soft-shadow"
                }`}
              >
                <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.2em]">
                  <span className={idx === 0 ? "text-indigo-300" : "text-indigo-700"}>{p.tag}</span>
                  <span className={`inline-flex items-center gap-1 ${idx === 0 ? "text-zinc-400" : "text-zinc-400"}`}>
                    · {p.read} read
                  </span>
                </div>
                <h2 className={`mt-3 font-display ${idx === 0 ? "text-3xl" : "text-xl"} font-semibold leading-tight tracking-tight ${idx === 0 ? "" : "text-zinc-900 group-hover:text-indigo-700"}`}>
                  {p.title}
                </h2>
                <p className={`mt-3 text-sm leading-relaxed flex-1 ${idx === 0 ? "text-zinc-300" : "text-zinc-600"}`}>
                  {p.excerpt}
                </p>
                <div className={`mt-5 inline-flex items-center gap-1 text-sm font-medium ${idx === 0 ? "text-indigo-300" : "text-indigo-600"}`}>
                  Read article <ArrowRight className="h-3.5 w-3.5" />
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Tag filter */}
        <div className="mt-12 flex flex-wrap gap-2" data-testid="blog-tags">
          {TAGS.map((t) => (
            <button
              key={t}
              onClick={() => setActive(t)}
              data-testid={`tag-${t}`}
              className={`rounded-full px-4 py-1.5 text-xs font-medium transition-colors ${
                active === t
                  ? "bg-zinc-900 text-white"
                  : "border border-zinc-200 bg-white text-zinc-700 hover:border-indigo-200 hover:text-indigo-700"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Article grid */}
        <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
          {(active === "All" ? rest : filtered).map((p) => (
            <Link
              key={p.slug}
              to={`/${p.slug}`}
              data-testid={`blog-${p.slug}`}
              className="group flex flex-col rounded-2xl border border-zinc-100 bg-white p-6 transition-all hover:-translate-y-0.5 hover:border-indigo-200 hover:shadow-md soft-shadow"
            >
              <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.2em]">
                <span className="text-indigo-700">{p.tag}</span>
                <span className="text-zinc-400">· {p.read} read</span>
              </div>
              <h2 className="mt-3 font-display text-lg font-semibold leading-tight tracking-tight text-zinc-900 group-hover:text-indigo-700">
                {p.title}
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-zinc-600 flex-1">{p.excerpt}</p>
              <div className="mt-4 flex items-center justify-between text-xs text-zinc-500">
                <span className="inline-flex items-center gap-1">
                  <Calendar className="h-3 w-3" /> {p.date}
                </span>
                <span className="inline-flex items-center gap-1 font-medium text-indigo-600">
                  Read <ArrowRight className="h-3 w-3" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </Layout>
  );
}
